import React, { useCallback, useEffect, useState } from 'react';
import { EuiFlexGroup, EuiFlexItem, EuiText, EuiCheckbox } from '@elastic/eui';
import { useSelector } from 'react-redux';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import ItemDetailsCollaspibleNav from './ItemDetailsCollaspibleNav';
import { ReactComponent as ItemExposeIcon } from '../../../../../assets/img/svg/ItemExposeIcon.svg';

const ItemExpose = () => {
    const editItem = useSelector((state) => state.menuReducer.activeItemDetails.details);
    const orderingModes = useSelector((state) => state.menuReducer.restaurantOrderingModes);
    const activeItemId = useSelector((state) => state.menu.activeItemId);
    const { control, setValue, watch } = useFormContext();
    const itemOrderingModes = useWatch({
        control,
        name: 'ordering_modes',
    });
    const isOutletMenu = useSelector((state) => state.menuReducer.isOutletMenu);
    const [orderingModeState, setOrderingModeState] = useState([]);
    const [removedOrderingModes, setRemovedOrderingModes] = useState([]);
    const [newOrderingModes, setNewOrderingModes] = useState([]);
    const [removeNewOrderingModes, setRemoveNewOrderingModes] = useState([]);
    const [isNavOpen, setIsNavOpen] = useState(false);
    const themeMode = useSelector((state) => state.theme.theme);
    useEffect(() => {
        if (itemOrderingModes?.length && activeItemId !== 'newId') {
            setOrderingModeState(itemOrderingModes);
        } else if (activeItemId === 'newId') {
            setOrderingModeState([]);
        }
    }, [itemOrderingModes, watch, editItem, activeItemId]);

    const activeOrderingModes = orderingModes?.filter((orderingMode) =>
        itemOrderingModes?.some(
            (itemMode) => itemMode.restaurant_ordering_mode_id === orderingMode.id && orderingMode.status
        )
    );

    useEffect(() => {
        setNewOrderingModes([]);
        setRemoveNewOrderingModes([]);
    }, [editItem, activeItemId]);

    const updateOrderingMode = useCallback(
        (mode, event) => {
            const doesHaveDifferentPrices = watch('is_enabled_same_price_details_for_all_ordering_modes');

            let selectedMode = {};
            selectedMode = {
                compare_price: !doesHaveDifferentPrices ? orderingModeState[0]?.compare_price : null,
                gst_slab: !doesHaveDifferentPrices ? orderingModeState[0]?.gst_slab : null,
                packaging_charge: !doesHaveDifferentPrices ? orderingModeState[0]?.packaging_charge : null,
                price: !doesHaveDifferentPrices ? orderingModeState[0]?.price : null,
                [`price_${mode.id}`]: !doesHaveDifferentPrices ? orderingModeState[0]?.price : null,
                restaurant_ordering_mode_id: mode?.id,
                restaurant_ordering_mode_name: mode?.display_name,
                time_slot_setting: !doesHaveDifferentPrices ? orderingModeState[0]?.time_slot_setting : null,
                time_slots: !doesHaveDifferentPrices ? orderingModeState[0]?.time_slots : null,
            };
            const isChecked = event.target.checked;
            // filterChecked(mode, orderingModeState);
            if (activeItemId !== 'newId') {
                if (isChecked) {
                    setValue('ordering_modes', [...orderingModeState, selectedMode]);
                    setOrderingModeState((prevState) => [...prevState, selectedMode]);
                } else {
                    const checkedOrderingModes = orderingModeState.filter(
                        (orderingMode) =>
                            orderingMode?.restaurant_ordering_mode_id !== selectedMode?.restaurant_ordering_mode_id
                    );
                    setOrderingModeState(checkedOrderingModes);
                    setValue('ordering_modes', checkedOrderingModes);
                }

                // Prepare the delete ordering array
                if (isChecked) {
                    const matchedModes = removedOrderingModes?.filter((orderingMode) => {
                        return orderingMode?.restaurant_ordering_mode_id !== selectedMode?.restaurant_ordering_mode_id;
                    });
                    setRemovedOrderingModes(matchedModes?.restaurant_ordering_mode_id);
                    setValue('delete_ordering_modes', matchedModes?.restaurant_ordering_mode_id);
                } else {
                    const matchedModes = editItem?.ordering_modes?.filter(
                        (orderingMode) =>
                            orderingMode?.restaurant_ordering_mode_id === selectedMode?.restaurant_ordering_mode_id
                    )?.[0];
                    setRemovedOrderingModes([
                        ...(removedOrderingModes?.length >= 0 ? removedOrderingModes : []),
                        matchedModes?.restaurant_ordering_mode_id,
                    ]);
                    setValue('delete_ordering_modes', [
                        ...(removedOrderingModes?.length >= 0 ? removedOrderingModes : []),
                        matchedModes.restaurant_ordering_mode_id,
                    ]);
                }
            } else {
                if (isChecked) {
                    setValue('ordering_modes', [...newOrderingModes, selectedMode]);
                    setNewOrderingModes((prevState) => [...prevState, selectedMode]);
                } else {
                    const checkedOrderingModes = newOrderingModes.filter(
                        (orderingMode) =>
                            orderingMode?.restaurant_ordering_mode_id !== selectedMode?.restaurant_ordering_mode_id
                    );
                    setNewOrderingModes(checkedOrderingModes);
                    setValue('ordering_modes', checkedOrderingModes);
                }

                // Prepare the delete ordering array
                if (isChecked) {
                    const matchedModes = removeNewOrderingModes?.filter((orderingMode) => {
                        return orderingMode?.restaurant_ordering_mode_id !== selectedMode?.restaurant_ordering_mode_id;
                    });
                    setRemoveNewOrderingModes(matchedModes?.restaurant_ordering_mode_id);
                    setValue('delete_ordering_modes', matchedModes?.restaurant_ordering_mode_id);
                } else {
                    const matchedModes = editItem?.ordering_modes?.filter(
                        (orderingMode) =>
                            orderingMode?.restaurant_ordering_mode_id === selectedMode?.restaurant_ordering_mode_id
                    )?.[0];
                    setRemoveNewOrderingModes([...removeNewOrderingModes, matchedModes?.restaurant_ordering_mode_id]);
                    setValue('delete_ordering_modes', [
                        ...removeNewOrderingModes,
                        matchedModes.restaurant_ordering_mode_id,
                    ]);
                }
            }
        },
        [
            watch,
            orderingModeState,
            activeItemId,
            setValue,
            removedOrderingModes,
            editItem?.ordering_modes,
            newOrderingModes,
            removeNewOrderingModes,
        ]
    );

    return (
        <>
            <ItemDetailsCollaspibleNav
                cardTitle='Expose Item'
                isNavOpen={isNavOpen}
                cardDescription={
                    <EuiFlexGroup
                        direction='column'
                        justifyContent='spaceBetween'
                        alignItems='flexStart'
                        gutterSize='xs'
                    >
                        {activeOrderingModes?.length ? (
                            <EuiFlexItem>
                                <EuiFlexGroup wrap responsive direction='row' alignItems='center' gutterSize='xs'>
                                    <EuiFlexItem grow={false}>
                                        <EuiText style={{ fontWeight: 600 }} size='xs'>
                                            Active Modes:{' '}
                                        </EuiText>{' '}
                                    </EuiFlexItem>
                                    {activeOrderingModes?.map((mode, index) => {
                                        return (
                                            <React.Fragment key={index.toString() + mode.id.toString()}>
                                                <EuiFlexItem grow={false}>
                                                    <EuiText size='xs'>
                                                        {mode.display_name}
                                                        {index + 1 !== activeOrderingModes?.length ? ',' : null}
                                                    </EuiText>
                                                </EuiFlexItem>
                                            </React.Fragment>
                                        );
                                    })}
                                </EuiFlexGroup>
                            </EuiFlexItem>
                        ) : (
                            <EuiText size='s'>Item turned off in all ordering modes</EuiText>
                        )}
                    </EuiFlexGroup>
                }
                setIsNavOpen={setIsNavOpen}
                toggleText='Edit Basic Details'
                icon={<ItemExposeIcon fill={themeMode === 'light' ? 'black' : 'white'} />}
            >
                <EuiFlexGroup
                    alignItems='center'
                    style={{ padding: '10px' }}
                    justifyContent='flexStart'
                    wrap
                    responsive
                >
                    {orderingModes
                        .filter((orderingMode) => orderingMode.status)
                        .map((mode) => {
                            return (
                                <EuiFlexItem key={mode.id.toString()}>
                                    <EuiFlexGroup direction='row' justifyContent='flexStart' alignItems='center'>
                                        <EuiFlexItem grow={false}>
                                            <Controller
                                                render={({ field }) => (
                                                    <EuiCheckbox
                                                        onChange={(e) => {
                                                            field.onChange(e.target.checked);
                                                            updateOrderingMode(mode, e);
                                                            setValue(
                                                                `${mode.display_name}_${mode.id}`,
                                                                e.target.checked
                                                            );
                                                        }}
                                                        id={`${mode.display_name}_${mode.id}`}
                                                        checked={Boolean(field.value)}
                                                        disabled={isOutletMenu}
                                                        compressed
                                                    />
                                                )}
                                                name={`${mode.display_name}_${mode.id}`}
                                                control={control}
                                            />
                                        </EuiFlexItem>
                                        <EuiFlexItem grow={false}>
                                            <EuiText style={{ textTransform: 'capitalize' }}>
                                                <label
                                                    htmlFor={`${mode.display_name}_${mode.id}`}
                                                    style={{ cursor: 'pointer', textTransform: 'capitalize' }}
                                                >
                                                    {mode.display_name}
                                                </label>
                                            </EuiText>
                                        </EuiFlexItem>
                                    </EuiFlexGroup>
                                </EuiFlexItem>
                            );
                        })}
                </EuiFlexGroup>
            </ItemDetailsCollaspibleNav>
        </>
    );
};

export default React.memo(ItemExpose);
